import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  .skills-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 40px;
    margin: 20px 0 0 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      grid-gap: 20px;
    }
  }

  .skills-column {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .skill-group {
    margin-bottom: 20px;

    .skill-category {
      margin: 0 0 15px 0;
      padding: 0;
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      font-weight: 600;
      color: var(--green);
      border-bottom: 1px solid var(--lightest-navy);
      padding-bottom: 5px;
    }

    ul {
      padding: 0;
      margin: 0;
      list-style: none;

      li {
        position: relative;
        margin-bottom: 8px;
        padding-left: 20px;
        font-family: var(--font-mono);
        font-size: var(--fz-xs);

        &:before {
          content: '▹';
          position: absolute;
          left: 0;
          color: var(--green);
          font-size: var(--fz-sm);
          line-height: 12px;
        }
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    // Custom config for About section - more aggressive on mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const config = {
      origin: 'bottom',
      distance: '20px',
      duration: 500,
      delay: isMobile ? 100 : 200,
      rotate: { x: 0, y: 0, z: 0 },
      opacity: 0,
      scale: 1,
      easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
      mobile: true,
      reset: false,
      useDelay: 'always',
      viewFactor: isMobile ? 0.05 : 0.25, // Very aggressive on mobile
      viewOffset: { top: 0, right: 0, bottom: 0, left: 0 },
    };

    sr.reveal(revealContainer.current, config);
  }, []);

  const skills = [
    {
      category: 'Languages & Runtime',
      items: ['Java', 'JavaScript', 'TypeScript', 'Rust (Axum, SQLx)', 'Dart', 'Node.js'],
    },
    {
      category: 'Frontend Frameworks',
      items: ['Next.js (App Router)', 'React', 'Vue', 'Svelte', 'React Native', 'Flutter'],
    },
    {
      category: 'Styling & UI',
      items: [
        'Tailwind CSS',
        'Shadcn UI',
        'Framer Motion',
        'Material UI',
        'NextUI',
        'Figma (UI/UX)',
      ],
    },
    {
      category: 'State Management & i18n',
      items: ['Zustand', 'Redux', 'Tolgee (i18n)'],
    },
    {
      category: 'Backend & API',
      items: ['Nest.js', 'Express', 'tRPC', 'GraphQL', 'OpenAPI', 'Swagger'],
    },
    {
      category: 'Databases & ORM',
      items: ['PostgreSQL', 'MongoDB', 'Supabase', 'Prisma', 'Drizzle ORM'],
    },
    {
      category: 'Authentication & Security',
      items: ['Better Auth (Auth.js)', 'Clerk', 'JWT', 'OAuth'],
    },
    {
      category: 'Cloud & Infrastructure',
      items: [
        'AWS (CLF-C02 Certified)',
        'HashiCorp Terraform (Certified Associate)',
        'Docker',
        'Microservices',
      ],
    },
    {
      category: 'Systems & DevOps',
      items: [
        'Linux (LPIC-1 Certified)',
        'Ubuntu/Debian',
        'Kernel-level debugging',
        'Git',
        'GitHub',
        'GitLab',
        'GitHub Actions (Custom Actions)',
        'GitLab Runners',
      ],
    },
    {
      category: 'Tools & Analytics',
      items: ['Sanity (CMS)', 'Posthog (Analytics)'],
    },
  ];

  // Split skills into two columns
  const firstColumn = skills.slice(0, 5);
  const secondColumn = skills.slice(5, 10);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! My name is Micheal and I'm a Full Stack Software Engineer dedicated to building
              high performance, scalable applications across web and native mobile platforms. My
              journey in software development began with a deep curiosity about how things work,
              leading me to master everything from frontend frameworks to systems level programming.
            </p>

            <p>
              Fast-forward to today, and I've built enterprise-level applications and robust systems
              that handle real-world challenges. My main focus these days is creating accessible,
              human centered products that combine elegant UI/UX with powerful backend architecture
              using modern technologies and cloud-native solutions.
            </p>

            <p>
              I'm also passionate about open-source contributions and have developed automation
              tools including GitHub Actions and complex backend systems. When I'm not coding,
              you'll find me exploring emerging technologies like AI/ML, Web3, and quantum
              computing.
            </p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <div className="skills-grid">
            <div className="skills-column">
              {firstColumn.map((category, categoryIndex) => (
                <div key={categoryIndex} className="skill-group">
                  <h4 className="skill-category">{category.category}</h4>
                  <ul>
                    {category.items.map((skill, skillIndex) => (
                      <li key={`${categoryIndex}-${skillIndex}`}>{skill}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="skills-column">
              {secondColumn.map((category, categoryIndex) => (
                <div key={categoryIndex + 5} className="skill-group">
                  <h4 className="skill-category">{category.category}</h4>
                  <ul>
                    {category.items.map((skill, skillIndex) => (
                      <li key={`${categoryIndex + 5}-${skillIndex}`}>{skill}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpeg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
